// ws.js — Client WebSocket : connexion WSS, authentification, file d'envoi
// fiable et reconnexion.
//
// v4.3.6 : les trames importantes émises pendant une reconnexion ne sont plus
// rejetées pendant la fenêtre OPEN mais non authentifiée. Elles restent dans
// une file mémoire bornée, puis sont envoyées dans l'ordre immédiatement après
// `auth-ok`. Les trames éphémères (typing/presence) ne sont jamais mises en
// file. Chaque callback est en outre lié au socket qui l'a créé : la fermeture
// tardive d'un ancien socket ne peut plus déconnecter ou doubler le socket actif.
import { openChallenge } from './crypto.js';

const SERVER_PING_INTERVAL = 30000; // le serveur pingue toutes les 30 s (src/ws.js)

export class WsClient {
  constructor(identity, handlers) {
    this.identity = identity;
    this.handlers = handlers;
    this.ws = null;
    this.authenticated = false;
    this.pendingTokenResolvers = new Map(); // reqId → { resolve, reject, timer }
    this.pendingRtcResolvers = new Map();
    this.outboundQueue = []; // v4.3.6 : trames fiables en attente d'authentification
    this.maxQueued = 500;
    this.reconnectDelay = 1000;
    this.closedByUser = false;
    this.deadline = null; // détection de socket silencieusement morte (Safari)
    this.reconnectTimer = null;
  }

  url() {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    return `${proto}://${location.host}/ws`;
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN && this.authenticated;
  }

  connect() {
    this.closedByUser = false;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    const socket = new WebSocket(this.url());
    this.ws = socket;
    socket.onopen = () => {
      if (socket !== this.ws) return;
      socket.send(JSON.stringify({ type: 'hello', pseudo: this.identity.pseudo }));
    };
    socket.onmessage = (ev) => {
      if (socket !== this.ws) return;
      this.armDeadline(socket);
      try {
        this.handle(JSON.parse(ev.data), socket);
      } catch (e) {
        this.handlers.onError?.(`Réponse serveur invalide : ${e.message}`);
      }
    };
    socket.onclose = () => this.onDown(socket);
    socket.onerror = () => { /* onclose suit toujours */ };
  }

  // Safari garde parfois un readyState OPEN sur une socket morte : si aucun
  // trafic (dont le keepalive JSON serveur) n'arrive pendant 2 intervalles,
  // on force la fermeture pour déclencher la reconnexion.
  armDeadline(socket = this.ws) {
    clearTimeout(this.deadline);
    this.deadline = setTimeout(() => {
      if (socket !== this.ws) return;
      try { socket.close(); } catch (_) {}
    }, SERVER_PING_INTERVAL * 2 + 5000);
  }

  onDown(socket) {
    if (socket !== this.ws) return;
    clearTimeout(this.deadline);
    this.authenticated = false;
    // Rejet immédiat des demandes en attente : l'appelant affiche une erreur
    // claire au lieu d'attendre un timeout silencieux.
    for (const [, p] of this.pendingTokenResolvers) {
      clearTimeout(p.timer);
      p.reject(new Error('Connexion au serveur perdue — reconnexion en cours, réessayez dans un instant.'));
    }
    this.pendingTokenResolvers.clear();
    for (const [, p] of this.pendingRtcResolvers) {
      clearTimeout(p.timer);
      p.reject(new Error('Connexion perdue avant la récupération de la configuration d’appel.'));
    }
    this.pendingRtcResolvers.clear();
    this.handlers.onStatus?.('disconnected');
    if (!this.closedByUser) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = setTimeout(() => this.connect(), this.reconnectDelay);
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
    }
  }

  handle(data, socket = this.ws) {
    switch (data.type) {
      case 'challenge': {
        const value = openChallenge(data.box, this.identity.publicKey, this.identity.privateKey);
        if (socket !== this.ws || socket.readyState !== WebSocket.OPEN) return;
        socket.send(JSON.stringify({ type: 'challenge-response', value }));
        break;
      }
      case 'auth-ok':
        this.authenticated = true;
        this.handlers.onAdminStatus?.(data.admin === true);
        this.reconnectDelay = 1000;
        this.armDeadline(socket);
        this.handlers.onStatus?.('connected');
        this.flushQueue();
        break;
      case 'keepalive':
        break; // trafic de maintien de session, aucun contenu — armDeadline déjà fait
      case 'message':
        this.handlers.onMessage?.(data);
        break;
      case 'sent':
        this.handlers.onSent?.(data);
        break;
      case 'delivered':
        this.handlers.onDelivered?.(data);
        break;
      case 'typing':
        // Éphémère : jamais stocké (ni serveur, ni IndexedDB).
        this.handlers.onTyping?.(data);
        break;
      case 'unsent':
        this.handlers.onUnsent?.(data);
        break;
      case 'key-rotated':
        this.handlers.onKeyRotated?.(data);
        break;
      case 'account-deleted':
        this.handlers.onAccountDeleted?.(data);
        break;
      case 'presence':
        this.handlers.onPresence?.(data);
        break;
      case 'call-signal':
        this.handlers.onCallSignal?.(data);
        break;
      case 'call-signal-sent':
        this.handlers.onCallSignalSent?.(data);
        break;
      case 'call-signal-error':
        this.handlers.onCallSignalError?.(data.error || 'Signal d’appel refusé.');
        break;
      case 'admin-preview':
        this.handlers.onAdminPreview?.(data);
        break;
      case 'admin-result':
        this.handlers.onAdminResult?.(data);
        break;
      case 'admin-error':
        this.handlers.onAdminError?.(data.error || 'Action administrateur refusée.');
        break;
      case 'rtc-config': {
        const p = this.pendingRtcResolvers.get(data.reqId);
        if (p) { clearTimeout(p.timer); p.resolve(data); this.pendingRtcResolvers.delete(data.reqId); }
        break;
      }
      case 'upload-token': {
        const p = this.pendingTokenResolvers.get(data.reqId);
        if (p) { clearTimeout(p.timer); p.resolve(data.token); this.pendingTokenResolvers.delete(data.reqId); }
        break;
      }
      case 'error':
        this.handlers.onError?.(data.error);
        break;
    }
  }

  // Les trames fiables sont conservées durant la reconnexion. Aucun envoi ne
  // touche le réseau avant la confirmation `auth-ok`.
  send(payload, { queue = true } = {}) {
    if (this.isConnected()) {
      this.ws.send(JSON.stringify(payload));
      return true;
    }
    if (!queue) return false;
    if (this.closedByUser) {
      throw new Error('Connexion fermée.');
    }
    if (this.outboundQueue.length >= this.maxQueued) {
      throw new Error('Trop de messages attendent la reconnexion. Réessayez après le retour en ligne.');
    }
    this.outboundQueue.push(payload);
    return false;
  }

  flushQueue() {
    if (!this.isConnected() || this.outboundQueue.length === 0) return;
    const queued = this.outboundQueue.splice(0);
    for (let i = 0; i < queued.length; i++) {
      if (!this.isConnected()) {
        this.outboundQueue.unshift(...queued.slice(i));
        return;
      }
      try {
        this.ws.send(JSON.stringify(queued[i]));
      } catch (_) {
        this.outboundQueue.unshift(...queued.slice(i));
        try { this.ws.close(); } catch (_) {}
        return;
      }
    }
  }

  ack(id) { try { this.send({ type: 'ack', id }); } catch (_) { /* conservé jusqu'à la reconnexion */ } }
  reportMessageFailure(id) { try { this.send({ type: 'message-failure', id }, { queue: false }); } catch (_) {} }
  typing(to) { try { this.send({ type: 'typing', to }, { queue: false }); } catch (_) {} }
  rotateKey(publicKey) { this.send({ type: 'rotate-key', publicKey }); }
  deleteAccount() { this.send({ type: 'delete-account' }); }
  adminPreview(action) { this.send({ type: 'admin-preview', action }, { queue: false }); }
  adminExecute(token, phrase) { this.send({ type: 'admin-execute', token, phrase }, { queue: false }); }
  unsend(id) { try { this.send({ type: 'unsend', id }); } catch (_) {} }
  askPresence(pseudo) { try { this.send({ type: 'presence', pseudo }, { queue: false }); } catch (_) {} }
  pushSubscribe(subscription) { this.send({ type: 'push-subscribe', subscription }); }
  pushUnsubscribe(endpoint) { this.send({ type: 'push-unsubscribe', endpoint }); }
  viewed(ids) { if (Array.isArray(ids) && ids.length) this.send({ type: 'viewed', ids }); }
  ackCallSignal(id) { try { this.send({ type: 'call-signal-ack', id }); } catch (_) {} }
  sendCallSignal(to, encrypted, { notify = false } = {}) {
    if (!this.isConnected()) throw new Error('Connexion serveur requise pour lancer ou négocier un appel.');
    const tmpId = crypto.randomUUID();
    this.send({ type: 'call-signal', to, tmpId, notify: !!notify, ...encrypted }, { queue: false });
    return tmpId;
  }

  getRtcConfig() {
    return new Promise((resolve, reject) => {
      if (!this.isConnected()) return reject(new Error('Connexion serveur requise.'));
      const reqId = crypto.randomUUID();
      const timer = setTimeout(() => {
        this.pendingRtcResolvers.delete(reqId);
        reject(new Error('Configuration d’appel indisponible.'));
      }, 10000);
      this.pendingRtcResolvers.set(reqId, { resolve, reject, timer });
      try {
        this.send({ type: 'rtc-config', reqId }, { queue: false });
      } catch (e) {
        clearTimeout(timer); this.pendingRtcResolvers.delete(reqId); reject(e);
      }
    });
  }

  getUploadToken() {
    return new Promise((resolve, reject) => {
      const reqId = crypto.randomUUID();
      const timer = setTimeout(() => {
        this.pendingTokenResolvers.delete(reqId);
        reject(new Error('Le serveur ne répond pas — vérifiez votre connexion et réessayez.'));
      }, 10000);
      this.pendingTokenResolvers.set(reqId, { resolve, reject, timer });
      try {
        this.send({ type: 'upload-token', reqId });
      } catch (e) {
        clearTimeout(timer);
        this.pendingTokenResolvers.delete(reqId);
        reject(e);
      }
    });
  }

  close() {
    this.closedByUser = true;
    clearTimeout(this.deadline);
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.outboundQueue = [];
    for (const [, p] of this.pendingRtcResolvers) { clearTimeout(p.timer); p.reject(new Error('Connexion fermée.')); }
    this.pendingRtcResolvers.clear();
    this.ws?.close();
  }
}
